<script lang="ts">
  // The dark left column of the login / sign-up pages: brand, tagline, the
  // eight blocks of the system and what they give the business. Shared by
  // Login.svelte and RequestAccess.svelte so the two never drift apart again.
  // Keys are spelled out literally (not built from a loop) so the i18n test
  // sees every one of them as live.
  import { _ } from "svelte-i18n";
</script>

{#snippet block(num: string, icon: string, title: string, sub: string)}
  <li class="block">
    <span class="block-icon"><span class="material-icons-outlined" aria-hidden="true">{icon}</span></span>
    <div class="block-body">
      <div class="block-title"><span class="block-num">{num}</span>{title}</div>
      <div class="block-sub">{sub}</div>
    </div>
  </li>
{/snippet}

{#snippet adv(title: string, sub: string)}
  <li>
    <span class="check"><span class="material-icons-outlined" aria-hidden="true">check</span></span>
    <div>
      <div class="adv-title">{title}</div>
      <div class="adv-sub">{sub}</div>
    </div>
  </li>
{/snippet}

<aside class="hero">
  <div class="brand">
    <img src="/logo_white.svg" alt="Order Evolution" class="brand-logo" />
  </div>
  <h1 class="tagline">{$_("app.tagline")}<br /><span class="accent">{$_("auth.tagline.line2")}</span></h1>
  <p class="lead">{$_("auth.hero.lead")}</p>

  <div class="section-title">{$_("auth.hero.blocksTitle")}</div>
  <ul class="blocks">
    {@render block("01", "dashboard", $_("auth.block.dashboard.title"), $_("auth.block.dashboard.sub"))}
    {@render block("02", "hub", $_("auth.block.partners.title"), $_("auth.block.partners.sub"))}
    {@render block("03", "inventory_2", $_("auth.block.catalog.title"), $_("auth.block.catalog.sub"))}
    {@render block("04", "sell", $_("auth.block.pricing.title"), $_("auth.block.pricing.sub"))}
    {@render block("05", "warehouse", $_("auth.block.warehouse.title"), $_("auth.block.warehouse.sub"))}
    {@render block("06", "receipt_long", $_("auth.block.orders.title"), $_("auth.block.orders.sub"))}
    {@render block("07", "storefront", $_("auth.block.storefront.title"), $_("auth.block.storefront.sub"))}
    {@render block("08", "tune", $_("auth.block.settings.title"), $_("auth.block.settings.sub"))}
  </ul>

  <div class="adv">
    <div class="section-title">{$_("auth.hero.advTitle")}</div>
    <ul class="adv-list">
      {@render adv($_("auth.adv.margin.title"), $_("auth.adv.margin.sub"))}
      {@render adv($_("auth.adv.manual.title"), $_("auth.adv.manual.sub"))}
      {@render adv($_("auth.adv.stock.title"), $_("auth.adv.stock.sub"))}
      {@render adv($_("auth.adv.clients.title"), $_("auth.adv.clients.sub"))}
      {@render adv($_("auth.adv.demand.title"), $_("auth.adv.demand.sub"))}
    </ul>
  </div>

  <div class="copyright">
    {$_("auth.copyright", { values: { year: new Date().getFullYear() } })}
    <span class="legal-links">
      <a href="#/privacy?from=app">{$_("footer.privacy")}</a>
      <span aria-hidden="true">·</span>
      <a href="#/terms?from=app">{$_("footer.terms")}</a>
    </span>
  </div>
</aside>

<style>
  .hero {
    background:
      linear-gradient(180deg, rgba(48, 56, 65, 0.9), rgba(33, 133, 213, 0.6)),
      url("/images/login_bg.jpg") center/cover no-repeat;
    color: var(--color-text-on-dark);
    padding: 40px 48px 32px;
    display: flex; flex-direction: column;
  }
  .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
  .brand-logo { height: 40px; width: auto; display: block; }
  .tagline { font-size: 32px; line-height: 1.15; margin: 0 0 12px; font-weight: 700; letter-spacing: -0.01em; }
  .accent { color: #9fd0f7; }
  .lead { color: rgba(255,255,255,0.86); margin: 0 0 28px; font-size: 15px; line-height: 1.5; max-width: 560px; }

  .section-title {
    font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(255,255,255,0.68); margin: 0 0 12px;
  }

  .blocks {
    list-style: none; padding: 0; margin: 0 0 28px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  }
  .block {
    display: flex; gap: 12px; align-items: flex-start;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    padding: 12px 14px;
  }
  .block-icon {
    flex-shrink: 0;
    width: 36px; height: 36px; border-radius: 8px;
    background: var(--color-primary); color: #fff;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .block-icon .material-icons-outlined { font-size: 20px; }
  .block-body { min-width: 0; }
  .block-title { font-size: 14px; font-weight: 700; line-height: 1.3; display: flex; gap: 8px; align-items: baseline; }
  .block-num { font-size: 11px; font-weight: 700; color: #9fd0f7; letter-spacing: 0.04em; }
  .block-sub { font-size: 12.5px; line-height: 1.4; opacity: 0.8; margin-top: 3px; }

  .adv { margin-bottom: 28px; }
  .adv-list { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; }
  .adv-list li { display: grid; grid-template-columns: 24px 1fr; column-gap: 10px; align-items: start; }
  .adv-list li:last-child { grid-column: 1 / -1; }
  .check {
    background: var(--color-primary); color: #fff;
    width: 24px; height: 24px; border-radius: var(--radius-pill);
    display: inline-flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 3px rgba(33,133,213,0.28);
    margin-top: 1px;
  }
  .check .material-icons-outlined { font-size: 16px; font-weight: 700; }
  .adv-title { font-size: 14px; font-weight: 700; line-height: 1.3; }
  .adv-sub { font-size: 12.5px; line-height: 1.4; opacity: 0.8; margin-top: 2px; }

  .copyright { margin-top: auto; font-size: 12px; opacity: 0.7; }
  .legal-links { display: inline-flex; gap: 8px; margin-left: 12px; }
  .legal-links a { color: inherit; text-decoration: underline; text-underline-offset: 2px; opacity: 0.85; }
  .legal-links a:hover { opacity: 1; }

  @media (max-width: 1200px) {
    .blocks { grid-template-columns: 1fr; }
    .adv-list { grid-template-columns: 1fr; }
    .adv-list li:last-child { grid-column: auto; }
  }
  @media (max-width: 900px) {
    .hero { padding: 32px 20px 24px; }
    .brand { margin-bottom: 24px; }
    .tagline { font-size: 26px; }
  }
</style>
